import { Stack, Typography, Divider, useTheme, DialogContent } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Card404, FormProvider, GlassEffectDialog, Scrollbar } from '@app/components';
import { IAddTaskBoardSchema, IProject, ITaskBoard } from '../types';
import { selectBoardDetailsState, selectTaskBoardState } from '../selector';
import { useRequestState } from '@app/hooks';
import { getTaskBoard } from '../slice';
import { selectMyProfileFormState } from '@app/modules/my-profile';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  setSelectedProject: (project: IProject) => void;
  openBoardId: number | null;
}

const BoardChangeDialog = ({ open, onClose, setSelectedProject, openBoardId }: Props) => {
  const methods = useForm<IAddTaskBoardSchema>();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { data } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });
  const { data: taskBoard } = useRequestState({
    stateSelector: selectTaskBoardState,
    errorShown: true,
  });
  const { data: myProfile } = useRequestState({
    stateSelector: selectMyProfileFormState,
    errorShown: true,
  });

  useEffect(() => {
    dispatch(getTaskBoard({ userId: myProfile?.id }));
  }, [dispatch, myProfile?.id]);

  const handleTaskBoardClick = (task: ITaskBoard) => {
    setSelectedProject(task as unknown as IProject);
    onClose();
  };

  return (
    <>
      <FormProvider methods={methods}>
        <GlassEffectDialog
          open={open}
          onClose={onClose}
          title={'Recent Boards'}
          backgroundImage={data?.image}
          padding={0}
          sx={{
            '& .MuiDialog-paper': {
              width: '30%',
              maxWidth: 'none',
              margin: 0,
            },
          }}
        >
          <Scrollbar sx={{ height: 450 }}>
            <DialogContent sx={{ width: '100%', padding: 2 }}>
              <Stack>
                {taskBoard?.length > 0 ? (
                  taskBoard?.map((task: ITaskBoard, index: number) => (
                    <Stack
                      key={task.id}
                      gap={1.5}
                      sx={{
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        handleTaskBoardClick(task);
                      }}
                    >
                      <Stack
                        gap={2}
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          cursor: 'pointer',
                          border:
                            task.id === openBoardId
                              ? `1px solid ${theme.palette.taskboard.raisinBlack}`
                              : 'none',
                          borderRadius: '8px',
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '20px',
                            height: '20px',
                            backgroundImage:
                              task.id === openBoardId ? 'url(/assets/images/polygon.png)' : 'none',
                            backgroundSize: 'cover',
                            borderTopRightRadius: '10%',
                          },
                        }}
                      >
                        <Stack
                          sx={{
                            height: '50px',
                            width: '50px',
                            backgroundColor: theme.palette.taskboard.raisinBlack,
                            backgroundImage: `url(${task?.image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            borderRadius: '10%',
                          }}
                        />
                        <Stack>
                          <Typography
                            sx={{
                              fontSize: '17px',
                              color: theme.palette.taskboard.darkSlateGray,
                              fontWeight: 600,
                            }}
                          >
                            {task?.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: theme.palette.taskboard.darkSlateGray,
                              fontWeight: 400,
                            }}
                          >
                            {`${task?.createdByUser?.firstName ?? ''} ${
                              task?.createdByUser?.lastName ?? ''
                            }'s workspace`}
                          </Typography>
                        </Stack>
                      </Stack>
                      {index !== taskBoard.length - 1 && (
                        <Divider sx={{ mb: 1.5, borderColor: theme.palette.grey[500] }} />
                      )}
                    </Stack>
                  ))
                ) : (
                  <Card404 height={250} message="Board Not Found!" />
                )}
              </Stack>
            </DialogContent>
          </Scrollbar>
        </GlassEffectDialog>
      </FormProvider>
    </>
  );
};

export { BoardChangeDialog };
